import React from 'react';
import { Lead } from '../types';
import Tooltip from './Tooltip';

interface LeadTableProps {
  leads: Lead[];
  onStatusUpdate: (leadId: number, newStatus: string) => void;
  onEditLead: (lead: Lead) => void;
  onCompraUpdate: (leadId: number, comprou: boolean) => void;
  onWhatsAppOpen: (lead: Lead) => void;
  selectedLeads?: number[];
  onLeadSelect?: (leadId: number, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  showBulkActions?: boolean;
}

const LeadTable: React.FC<LeadTableProps> = ({ 
  leads, 
  onStatusUpdate, 
  onEditLead, 
  onCompraUpdate, 
  onWhatsAppOpen,
  selectedLeads = [],
  onLeadSelect,
  onSelectAll,
  showBulkActions = false
}) => {
  const statusOptions = [
    'novo',
    'contato_inicial',
    'em_negociacao',
    'proposta_enviada',
    'fechado_ganho',
    'fechado_perdido',
    'arquivado'
  ];

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'bg-blue-100 text-blue-800',
      contato_inicial: 'bg-yellow-100 text-yellow-800',
      em_negociacao: 'bg-orange-100 text-orange-800',
      proposta_enviada: 'bg-purple-100 text-purple-800',
      fechado_ganho: 'bg-green-100 text-green-800',
      fechado_perdido: 'bg-red-100 text-red-800',
      arquivado: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            {showBulkActions && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectedLeads.length === leads.length && leads.length > 0}
                  onChange={(e) => onSelectAll?.(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Processo
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Classe/Assunto
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Partes
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Réu/Contato
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Local/Valor
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status/Consultor
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              📝 Observações
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {leads.map((lead) => (
            <tr key={lead.id} className="hover:bg-gray-50">
              {showBulkActions && (
                <td className="px-4 py-4 text-sm font-medium text-gray-900">
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={(e) => onLeadSelect?.(lead.id, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </td>
              )}
              <td className="px-4 py-4 text-sm font-medium text-gray-900">
                <div className="space-y-1">
                  <div className="font-semibold">{lead.numero_processo}</div>
                  <div className="text-xs text-gray-500">
                    {lead.localidade || 'N/A'} | {lead.juiz || 'N/A'}
                  </div>
                  {lead.data_autuacao_detalhada && (
                    <div className="text-xs text-blue-600">
                      Autuação: {lead.data_autuacao_detalhada}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">
                <div className="space-y-1">
                  <div className="font-medium text-gray-900">{lead.classe || 'N/A'}</div>
                  <div className="text-xs">{lead.assunto || 'N/A'}</div>
                  {lead.situacao && (
                    <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block">
                      {lead.situacao}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-400 uppercase">Réu:</div>
                    <div className="font-medium text-gray-900">{lead.reu_nome || lead.reu || 'N/A'}</div>
                    {lead.reu_cpf && <div className="text-xs">CPF: {lead.reu_cpf}</div>}
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase">Autor:</div>
                    <div className="font-medium text-gray-700">{lead.autor_nome || 'N/A'}</div>
                    {lead.autor_cpf && <div className="text-xs">CPF: {lead.autor_cpf}</div>}
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-400 uppercase">Telefones:</div>
                    {[lead.reu_telefone_1, lead.reu_telefone_2, lead.reu_telefone_3].filter(Boolean).map((phone, idx) => (
                      <div key={idx} className="text-xs flex items-center gap-1">
                        {formatPhone(phone)}
                        {(lead.r_whats_1 && idx === 0) || (lead.r_whats_2 && idx === 1) || (lead.r_whats_3 && idx === 2) ? (
                          <span className="text-green-600">📱</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  {lead.reu_data_nascimento && (
                    <div className="text-xs">
                      <span className="text-gray-400">Nasc:</span> {lead.reu_data_nascimento}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-400 uppercase">Localidade:</div>
                    <div className="font-medium">{lead.localidade || 'N/A'}</div>
                    {(lead.reu_cidade || lead.reu_estado) && (
                      <div className="text-xs">{lead.reu_cidade}, {lead.reu_estado}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase">Valor:</div>
                    <div className="font-semibold text-green-600">{formatCurrency(lead.valor_causa)}</div>
                    {lead.justica_gratuita && (
                      <div className="text-xs bg-yellow-50 text-yellow-700 px-1 rounded">
                        Justiça Gratuita
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 text-sm">
                <div className="space-y-2">
                  <select
                    value={lead.status_lead || 'novo'}
                    onChange={(e) => onStatusUpdate(lead.id, e.target.value)}
                    className={`text-xs rounded-full px-2 py-1 font-semibold w-full ${getStatusColor(lead.status_lead || 'novo')}`}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs">
                    <span className="text-gray-400">Consultor:</span>
                    <div className="font-medium">{lead.consultor_nome || 'Não atribuído'}</div>
                  </div>
                  {lead.comprou && (
                    <div className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                      ✓ Comprado
                    </div>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500 max-w-xs">
                <div className="space-y-2">
                  {lead.tags && lead.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {lead.tags.slice(0, 2).map(tag => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: `${tag.color}20`, 
                            color: tag.color,
                            border: `1px solid ${tag.color}40`
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                      {lead.tags.length > 2 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{lead.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                  {lead.observacoes ? (
                    <Tooltip content={lead.observacoes}>
                      <div className="truncate cursor-help">
                        {lead.observacoes.length > 60 
                          ? `${lead.observacoes.substring(0, 60)}...`
                          : lead.observacoes
                        }
                      </div>
                    </Tooltip>
                  ) : (
                    <span className="text-gray-400 italic">Sem anotações</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm font-medium">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => onEditLead(lead)}
                    className="text-indigo-600 hover:text-indigo-900 text-xs"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => onWhatsAppOpen(lead)}
                    className="text-green-600 hover:text-green-900 text-xs flex items-center gap-1"
                  >
                    📱 WhatsApp
                  </button>
                  <button
                    onClick={() => onCompraUpdate(lead.id, !lead.comprou)}
                    className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                      lead.comprou 
                        ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {lead.comprou ? '✓ Comprado' : '○ Não comprado'}
                  </button>
                  {lead.comprou && lead.data_venda && (
                    <div className="text-xs text-green-600">
                      💰 {formatDate(lead.data_venda)}
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    {formatDate(lead.data_importacao)}
                  </div>
                  {lead.total_movimentos && (
                    <div className="text-xs bg-gray-50 text-gray-600 px-1 rounded">
                      {lead.total_movimentos} mov.
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {leads.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Nenhuma lead encontrada
        </div>
      )}
    </div>
  );
};

export default LeadTable;